import type { Project, ProjectJob, UsageSummary } from '../../../lib/types';
import { record, rows } from '../production/model';

const amount = (value: unknown) => typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null;
export function recordedJobCost(job: ProjectJob) {
  const usage = record(record(job.result).usage);
  const inr = amount(job.costINR);
  if (inr !== null) return { amount: inr, currency: 'INR', estimated: false };
  for (const [value, estimated] of [[job.actualCost, false], [usage.actualCost, false], [job.estimatedCost, true], [usage.estimatedCost, true]] as const) {
    const cost = amount(value);
    if (cost !== null) return { amount: cost, currency: 'USD', estimated };
  }
  return null;
}

export type LedgerRun = ReturnType<typeof projectLedger>[number];

export function projectLedger(project: Project) {
  const unique = new Map((project.jobs || []).map(job => [job.id, job]));
  const jobs = [...unique.values()].filter(job => ['complete', 'completed'].includes(String(job.status)));
  return jobs.map(job => {
    const payload = record(job.payload), lineage = { ...record(payload.lineage), ...record(job.lineage) };
    return { id: job.id, task: String(payload.task || job.type || job.kind || 'Run'), scene: String(payload.sceneId || lineage.sceneId || ''), shot: String(payload.shotId || lineage.shotId || ''), user: String(job.createdBy || ''), at: String(job.completedAt || job.updatedAt || job.createdAt || ''), cost: recordedJobCost(job) };
  }).sort((a, b) => (b.cost?.amount ?? -1) - (a.cost?.amount ?? -1));
}

export function ledgerTotals(runs: LedgerRun[]) {
  const totals = new Map<string, number>();
  for (const run of runs) if (run.cost) totals.set(run.cost.currency, (totals.get(run.cost.currency) || 0) + run.cost.amount);
  return { totals: [...totals].sort((a, b) => b[1] - a[1]), unpriced: runs.filter(run => !run.cost).length };
}

export type BreakdownKey = 'task' | 'scene' | 'shot' | 'user' | 'model';
export interface BreakdownRow { key: string; name: string; value: number | null; count: number | null }

export function costBreakdown(value: unknown, label: (key: string) => string = key => key): BreakdownRow[] {
  return Object.entries(record(value)).map(([key, detail]) => ({ key, name: label(key), value: amount(record(detail).cost), count: amount(record(detail).count) })).sort((a, b) => (b.value ?? -1) - (a.value ?? -1));
}

export function modelBreakdown(usage: UsageSummary): BreakdownRow[] {
  return Object.entries(usage.modelBreakdown || {}).map(([key, detail]) => {
    const entry = typeof detail === 'number' ? { credits: detail } : detail;
    return { key, name: key, value: amount(entry.credits), count: amount(entry.count) };
  }).sort((a, b) => (b.value ?? -1) - (a.value ?? -1));
}

export function shotAndSceneNames(projects: Project[]) {
  const names = new Map<string, string>();
  for (const project of projects) {
    for (const [index, scene] of rows(project.scenes).entries()) if (scene.id) names.set(String(scene.id), `${project.name} · ${String(scene.title || scene.heading || scene.name || `Scene ${index + 1}`)}`);
    for (const [index, shot] of (project.shots || []).entries()) names.set(shot.id, `${project.name} · ${shot.name || `Shot ${String(index + 1).padStart(2, '0')}`}`);
  }
  return (key: string) => names.get(key) || key;
}

export const parseAmount = (value: unknown) => {
  const parsed = Number(String(value ?? '').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
};

export const currencyAmount = (value: number, currency = 'INR') => new Intl.NumberFormat(undefined, { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);

export const quoteInr = (value: number) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: Math.abs(value) >= 1 ? 2 : 4 }).format(value);

export const compactNumber = (value: number) => new Intl.NumberFormat(undefined, { notation: value >= 100_000 ? 'compact' : 'standard', maximumFractionDigits: value >= 100_000 ? 1 : 0 }).format(value);
